import { Stack, useMantineTheme } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { DisplayCreative, FacebookDisplayCreative } from '@feathr/blackbox';
import { CreativeClass, isAdTagCreative } from '@feathr/blackbox';
import { Button, Input, Toggle } from '@feathr/components';

import { CreativeDimensionsWell } from '../../../CreativeDimensionsWell';
import AdTagFields from './AdTagFields';
import MetaFields from './MetaFields';

import * as styles from '../CreativeForm.css';

interface ISetupTabProps {
  creative: DisplayCreative;
  /** Campaign ID, used to filter CTA options by campaign objective. */
  campaignId?: string;
  /** Callback to open Bannersnack editor for builder-created creatives */
  onEditInBannersnack?: () => void;
}

function SetupTab({
  creative,
  campaignId,
  onEditInBannersnack,
}: Readonly<ISetupTabProps>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const isMeta = creative.channel === 'meta';
  const isBannersnack = creative.get('_cls') === CreativeClass.DisplayBannersnack;

  return (
    <Stack gap={theme.spacing['4']}>
      <CreativeDimensionsWell creative={creative} />

      <Input
        attribute={'name'}
        label={t('Creative name')}
        model={creative}
        placeholder={t('Enter name')}
        type={'text'}
      />

      {!isMeta && isBannersnack && onEditInBannersnack && (
        <Button
          className={styles.fullWidth}
          name={'edit-in-bannersnack'}
          onClick={onEditInBannersnack}
          type={'secondary'}
        >
          {t('Edit in Bannersnack')}
        </Button>
      )}

      <Toggle
        label={t('Enable creative')}
        layout={'well'}
        onChange={(value) => creative.set({ is_enabled: !!value })}
        value={creative.get('is_enabled')}
      />

      {!isMeta && isAdTagCreative(creative) && <AdTagFields creative={creative} />}

      {isMeta && (
        <MetaFields campaignId={campaignId} creative={creative as FacebookDisplayCreative} />
      )}
    </Stack>
  );
}

export default observer(SetupTab);
